import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CurrencyContext = createContext();

// Map countries to their currencies
const COUNTRY_CURRENCY_MAP = {
  'NZ': 'NZD',
  'AU': 'AUD',
  'US': 'USD',
  'GB': 'GBP',
  'CA': 'CAD',
  'EU': 'EUR',
  'DE': 'EUR',
  'FR': 'EUR',
  'IT': 'EUR',
  'ES': 'EUR',
  'NL': 'EUR',
  'BE': 'EUR',
  'AT': 'EUR',
  'PT': 'EUR',
  'IE': 'EUR',
  'JP': 'JPY',
  'CN': 'CNY',
  'IN': 'INR',
  'SG': 'SGD',
  'HK': 'HKD',
  'ZA': 'ZAR',
  'NG': 'NGN',
  'GH': 'GHS',
  'KE': 'KES',
  'ET': 'ETB',
  'CH': 'CHF',
  'SE': 'SEK',
  'NO': 'NOK',
  'DK': 'DKK',
  'MX': 'MXN',
  'BR': 'BRL',
  'AR': 'ARS',
  'CL': 'CLP',
  'KR': 'KRW',
  'TH': 'THB',
  'MY': 'MYR',
  'PH': 'PHP',
  'ID': 'IDR',
  'VN': 'VND',
  'AE': 'AED',
  'SA': 'SAR',
  'IL': 'ILS',
  'TR': 'TRY',
  'PL': 'PLN',
  'CZ': 'CZK',
  'HU': 'HUF',
  'RO': 'RON',
  'RU': 'RUB'
};

// Currency symbols
const CURRENCY_SYMBOLS = {
  'NZD': 'NZ$',
  'AUD': 'A$',
  'USD': '$',
  'GBP': '£',
  'CAD': 'C$',
  'EUR': '€',
  'JPY': '¥',
  'CNY': '¥',
  'INR': '₹',
  'SGD': 'S$',
  'HKD': 'HK$',
  'ZAR': 'R',
  'NGN': '₦',
  'GHS': 'GH₵',
  'KES': 'KSh',
  'ETB': 'Br',
  'CHF': 'CHF',
  'SEK': 'kr',
  'NOK': 'kr',
  'DKK': 'kr',
  'MXN': 'Mex$',
  'BRL': 'R$',
  'ARS': 'ARS$',
  'CLP': 'CLP$',
  'KRW': '₩',
  'THB': '฿',
  'MYR': 'RM',
  'PHP': '₱',
  'IDR': 'Rp',
  'VND': '₫',
  'AED': 'AED',
  'SAR': 'SAR',
  'ILS': '₪',
  'TRY': '₺',
  'PLN': 'zł',
  'CZK': 'Kč',
  'HUF': 'Ft',
  'RON': 'lei',
  'RUB': '₽'
};

// Open Exchange Rates configuration
const OPEN_EXCHANGE_RATES_APP_ID = process.env.REACT_APP_OPEN_EXCHANGE_RATES_API_KEY || 'YOUR_API_KEY_HERE';
const OPEN_EXCHANGE_RATES_BASE_URL = 'https://openexchangerates.org/api/latest.json';

// LocalStorage keys
const STORAGE_KEY_RATES = 'currency_exchange_rates';
const STORAGE_KEY_TIMESTAMP = 'currency_rates_timestamp';
const RETRY_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds

// Regional Price Adjustment Factors
// Format: [product-id][country-code] = factor
// Factor: 1.0 = no adjustment, >1.0 = markup, <1.0 = discount
const REGIONAL_PRICE_FACTORS = {
  // Linux Server Setup (Promo1 - NZ$249)
  'linux-server-setup': {
    'NZ': 1.0,   // New Zealand - base price
    'AU': 1.0,   // Australia - standard
    'US': 1.0,   // USA - standard
    'GB': 1.0,   // UK - standard
    'CA': 1.0,   // Canada - standard
    'NG': 0.25,   // Nigeria - TODO: adjust as needed
    'GH': 1.0,   // Ghana - TODO: adjust as needed
    'KE': 1.0,   // Kenya - TODO: adjust as needed
    'ET': 1.0,   // Ethiopia - TODO: adjust as needed
    'ZA': 1.0,   // South Africa - TODO: adjust as needed
    // Add more countries as needed
  },

  // Vehicle Telematics Device + Monthly (Promo2)
  'telematics-device-monthly': {
    'NZ': 1.0,   // New Zealand - base price
    'AU': 1.0,   // Australia - standard
    'US': 1.0,   // USA - standard
    'GB': 1.0,   // UK - standard
    'CA': 1.0,   // Canada - standard
    'NG': 1.0,   // Nigeria - TODO: adjust as needed
    'GH': 1.0,   // Ghana - TODO: adjust as needed
    'KE': 1.0,   // Kenya - TODO: adjust as needed
    'ET': 1.0,   // Ethiopia - TODO: adjust as needed
    'ZA': 1.0,   // South Africa - TODO: adjust as needed
  },
	
  // Website Landing Page Build + Host (Promo3)
  'landing-page-hosting': {
    'NZ': 1.0,   // New Zealand - base price
    'AU': 1.0,   // Australia - standard
    'US': 1.0,   // USA - standard
    'GB': 1.0,   // UK - standard
    'CA': 1.0,   // Canada - standard
    'NG': 1.0,   // Nigeria - TODO: adjust as needed
    'GH': 1.0,   // Ghana - TODO: adjust as needed
    'KE': 1.0,   // Kenya - TODO: adjust as needed
    'ET': 1.0,   // Ethiopia - TODO: adjust as needed
    'ZA': 1.0,   // South Africa - TODO: adjust as needed
  },

  // Add more products here as you implement them
  // Example:
  // 'product-id': {
  //   'US': 0.8,  // 20% discount for USA
  //   'NG': 1.5,  // 50% markup for Nigeria
  // }
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('NZD'); // Default to NZD
  const [exchangeRates, setExchangeRates] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [userCountry, setUserCountry] = useState('NZ');
  const [lastFetchSuccess, setLastFetchSuccess] = useState(true);
  const [retryIntervalId, setRetryIntervalId] = useState(null);

  // Load cached rates from localStorage
  const loadCachedRates = useCallback(() => {
    try {
      const cachedRates = localStorage.getItem(STORAGE_KEY_RATES);
      const cachedTimestamp = localStorage.getItem(STORAGE_KEY_TIMESTAMP);

      if (cachedRates) {
        const rates = JSON.parse(cachedRates);
        console.log('Loaded cached exchange rates from localStorage', {
          timestamp: cachedTimestamp,
          ratesCount: Object.keys(rates).length
        });
        return rates;
      }
    } catch (error) {
      console.error('Failed to load cached rates from localStorage:', error);
    }
    return null;
  }, []);

  // Save rates to localStorage
  const saveCachedRates = useCallback((rates) => {
    try {
      localStorage.setItem(STORAGE_KEY_RATES, JSON.stringify(rates));
      localStorage.setItem(STORAGE_KEY_TIMESTAMP, new Date().toISOString());
      console.log('Saved exchange rates to localStorage');
    } catch (error) {
      console.error('Failed to save rates to localStorage:', error);
    }
  }, []);

  // Convert Open Exchange Rates format (USD base) to NZD base
  const convertToNZDBase = useCallback((usdRates) => {
    const nzdRate = usdRates.NZD;
    if (!nzdRate) return usdRates;

    const nzdBasedRates = {};
    for (const [currency, rate] of Object.entries(usdRates)) {
      // Convert from USD base to NZD base
      nzdBasedRates[currency] = rate / nzdRate;
    }

    return nzdBasedRates;
  }, []);

  // Fetch exchange rates from Open Exchange Rates API
  const fetchExchangeRates = useCallback(async () => {
    try {
      console.log('Fetching exchange rates from Open Exchange Rates...');
      console.log('API Key configured:', OPEN_EXCHANGE_RATES_APP_ID !== 'YOUR_API_KEY_HERE' ? 'YES' : 'NO');
      console.log('API URL:', `${OPEN_EXCHANGE_RATES_BASE_URL}?app_id=${OPEN_EXCHANGE_RATES_APP_ID.substring(0, 8)}...`);

      const response = await fetch(
        `${OPEN_EXCHANGE_RATES_BASE_URL}?app_id=${OPEN_EXCHANGE_RATES_APP_ID}`
      );

      console.log('API Response Status:', response.status, response.statusText);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('API Error Details:', errorData);
        throw new Error(`API Error ${response.status}: ${errorData.message || response.statusText}`);
      }

      const data = await response.json();
      console.log('API Response received:', {
        timestamp: data.timestamp,
        base: data.base,
        ratesCount: Object.keys(data.rates || {}).length
      });

      // Open Exchange Rates uses USD as base, convert to NZD base
      const nzdBasedRates = convertToNZDBase(data.rates);

      setExchangeRates(nzdBasedRates);
      saveCachedRates(nzdBasedRates);
      setLastFetchSuccess(true);
      setIsLoading(false);

      console.log('Exchange rates fetched successfully from API');

      // Clear retry interval if it exists
      if (retryIntervalId) {
        clearInterval(retryIntervalId);
        setRetryIntervalId(null);
      }

      return true;
    } catch (error) {
      console.error('Failed to fetch exchange rates from API:', error);
      setLastFetchSuccess(false);

      // Try to load from cache
      const cachedRates = loadCachedRates();
      if (cachedRates && Object.keys(cachedRates).length > 0) {
        console.log('Using cached exchange rates as fallback');
        setExchangeRates(cachedRates);
        setIsLoading(false);
      } else {
        console.error('No cached rates available - this should not happen if user has visited before');
        setIsLoading(false);
      }

      return false;
    }
  }, [convertToNZDBase, loadCachedRates, saveCachedRates, retryIntervalId]);

  // Set up retry mechanism for failed fetches
  useEffect(() => {
    if (!lastFetchSuccess && !retryIntervalId) {
      console.log(`API fetch failed. Will retry every ${RETRY_INTERVAL / 1000 / 60} minutes...`);

      const intervalId = setInterval(() => {
        console.log('Retrying exchange rate fetch...');
        fetchExchangeRates();
      }, RETRY_INTERVAL);

      setRetryIntervalId(intervalId);

      // Cleanup on unmount
      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    }
  }, [lastFetchSuccess, retryIntervalId, fetchExchangeRates]);

  // Detect user's country and currency
  useEffect(() => {
    const detectCountryAndCurrency = async () => {
      try {
        // Try to get country from your backend (which reads Cloudflare headers)
        const response = await fetch('/api/detect-country');
        const data = await response.json();

        if (data.country) {
          const detectedCountry = data.country;
          const detectedCurrency = COUNTRY_CURRENCY_MAP[detectedCountry] || 'NZD';

          setUserCountry(detectedCountry);
          setCurrency(detectedCurrency);

          console.log(`Detected country: ${detectedCountry}, Currency: ${detectedCurrency}`);
        }
      } catch (error) {
        console.log('Country detection failed, using browser-based detection');

        // Fallback: Try browser language/timezone detection
        const browserLang = navigator.language || navigator.userLanguage;
        let detectedCountry = 'NZ';
        let detectedCurrency = 'NZD';

        // Map common browser languages to countries/currencies
        if (browserLang.startsWith('en-US')) {
          detectedCountry = 'US';
          detectedCurrency = 'USD';
        } else if (browserLang.startsWith('en-AU')) {
          detectedCountry = 'AU';
          detectedCurrency = 'AUD';
        } else if (browserLang.startsWith('en-GB')) {
          detectedCountry = 'GB';
          detectedCurrency = 'GBP';
        } else if (browserLang.startsWith('en-CA')) {
          detectedCountry = 'CA';
          detectedCurrency = 'CAD';
        } else if (browserLang.startsWith('de')) {
          detectedCountry = 'DE';
          detectedCurrency = 'EUR';
        } else if (browserLang.startsWith('fr')) {
          detectedCountry = 'FR';
          detectedCurrency = 'EUR';
        } else if (browserLang.startsWith('es')) {
          detectedCountry = 'ES';
          detectedCurrency = 'EUR';
        } else if (browserLang.startsWith('ja')) {
          detectedCountry = 'JP';
          detectedCurrency = 'JPY';
        } else if (browserLang.startsWith('zh')) {
          detectedCountry = 'CN';
          detectedCurrency = 'CNY';
        }

        console.log(`Browser-based detection: ${browserLang} → ${detectedCountry} (${detectedCurrency})`);
        setUserCountry(detectedCountry);
        setCurrency(detectedCurrency);
      }
    };

    detectCountryAndCurrency();
  }, []);

  // Initial fetch of exchange rates
  useEffect(() => {
    // First, try to load cached rates immediately for fast initial render
    const cachedRates = loadCachedRates();
    if (cachedRates && Object.keys(cachedRates).length > 0) {
      console.log('Using cached rates for initial render');
      setExchangeRates(cachedRates);
      setIsLoading(false);
    }

    // Then fetch fresh rates from API
    fetchExchangeRates();

    // Refresh rates every 24 hours (if API is working)
    const dailyInterval = setInterval(() => {
      if (lastFetchSuccess) {
        console.log('Daily refresh of exchange rates...');
        fetchExchangeRates();
      }
    }, 24 * 60 * 60 * 1000);

    return () => clearInterval(dailyInterval);
  }, [fetchExchangeRates, loadCachedRates, lastFetchSuccess]);

  // Convert NZD price to user's currency with optional regional adjustment
  const convertPrice = useCallback((nzdPrice, productId = null) => {
    if (!exchangeRates[currency]) {
      console.warn(`No exchange rate available for ${currency}, using 1:1 ratio`);
      return nzdPrice;
    }

    const rate = exchangeRates[currency];
    let converted = nzdPrice * rate;

    // Apply regional price adjustment factor if product ID is provided
    if (productId && REGIONAL_PRICE_FACTORS[productId]) {
      const regionalFactor = REGIONAL_PRICE_FACTORS[productId][userCountry] || 1.0;
      converted = converted * regionalFactor;

      if (regionalFactor !== 1.0) {
        console.log(`Applied regional factor for ${productId} in ${userCountry}: ${regionalFactor}`);
      }
    }

    // Round to sensible values based on currency
    if (['JPY', 'KRW', 'IDR', 'VND'].includes(currency)) {
      // Currencies without decimals
      return Math.round(converted);
    } else {
      // Round to nearest dollar for most currencies
      return Math.round(converted);
    }
  }, [currency, exchangeRates, userCountry]);

  // Format price with currency symbol and optional regional adjustment
  const formatPrice = useCallback((nzdPrice, options = {}) => {
    const {
      showCurrency = true,
      showSymbol = true,
      productId = null
    } = options;

    const convertedAmount = convertPrice(nzdPrice, productId);
    const symbol = CURRENCY_SYMBOLS[currency] || currency;

    let formatted = '';

    if (showSymbol) {
      formatted = `${symbol}${convertedAmount.toLocaleString()}`;
    } else {
      formatted = convertedAmount.toLocaleString();
    }

    if (showCurrency && currency !== 'NZD') {
      formatted += ` ${currency}`;
    }

    return formatted;
  }, [currency, convertPrice]);

  const value = {
    currency,
    userCountry,
    exchangeRates,
    isLoading,
    convertPrice,
    formatPrice,
    setCurrency, // Allow manual override if needed
    currencySymbol: CURRENCY_SYMBOLS[currency] || currency,
    lastFetchSuccess
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
};
